import { T } from '@transifex/react';
import { Heading, Text, VStack } from '@vidahealth/vida-ui';
import { Text as RNText, Linking, Platform } from 'react-native';

import { BottomSheet, CtaButton } from '@/modules/ui';

import { SUPPORT_PHONE } from '../constants';

type ExistingAccountBottomSheetProps = {
  isOpen: boolean;
  onClose: () => void;
  onLogin: () => void;
  /**
   * The server's own (untranslated) message from a plain email-uniqueness
   * failure — used as the heading. Absent for the ALREADY_MATCHED case, where
   * the heading falls back to the fixed "You already have an account" copy
   * resolved here with `<T>` (so it re-translates if Transifex pushes
   * mid-drawer, rather than capturing the string at error-time).
   */
  serverMessage?: string | null;
  /**
   * Masked email of the matched account (ALREADY_MATCHED) — shown so the user
   * can recognize which account to log in to. Absent for the plain
   * email-uniqueness error.
   */
  maskedEmail?: string | null;
};

export function ExistingAccountBottomSheet({
  isOpen,
  onClose,
  onLogin,
  serverMessage,
  maskedEmail,
}: ExistingAccountBottomSheetProps) {
  return (
    <BottomSheet isOpen={isOpen} onClose={onClose} heightRatio={0.55}>
      <VStack space='2xl'>
        <Heading
          size='lg'
          className='flex-1 font-heading-semibold text-primary-800 dark:text-primary-0'
        >
          {serverMessage ?? <T _str='You already have an account' />}
        </Heading>

        <Text size='md' className='text-typography-900'>
          {maskedEmail ? (
            <T _str='The information you entered is already associated with an account. Use the email below to log in to your account.' />
          ) : (
            <T _str='The information you entered is already associated with an account. Please log in to your account to continue.' />
          )}
        </Text>

        {maskedEmail ? (
          <Text size='md' className='font-body-semibold text-typography-900'>
            {maskedEmail}
          </Text>
        ) : null}

        <CtaButton onPress={onLogin} testID='existing-account-login'>
          <T _str='Log in' />
        </CtaButton>

        <Text size='sm' className='text-typography-600'>
          <T
            _str='Trying to sign up dependents, edit your email, or need help in general? Call {phone}.'
            phone={
              Platform.OS === 'web' ? (
                <Text size='sm' className='text-typography-600'>
                  {SUPPORT_PHONE}
                </Text>
              ) : (
                <RNText
                  onPress={() => Linking.openURL(`tel:${SUPPORT_PHONE.replace(/\D/g, '')}`)}
                  testID='existing-account-support'
                  className='text-sm text-primary-500'
                  role='link'
                >
                  {SUPPORT_PHONE}
                </RNText>
              )
            }
          />
        </Text>
      </VStack>
    </BottomSheet>
  );
}
